import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ApiAuthError,
  fetchOutcomeSeries,
  fetchSessions,
  fetchTaskBreakdown,
  type DashboardWindow,
} from '../api/client';
import {
  EMPTY_SESSION_FILTERS,
  countActiveSessionFilters,
  type SessionFilters,
  type SessionSortDir,
  type SessionSortField,
  type SessionSummary,
} from '../api/types';
import { SignInScreen } from '../auth/SignInScreen';
import { useToken } from '../auth/TokenContext';
import { OutcomeStackedBarChart } from '../components/charts/OutcomeStackedBarChart';
import { TaskBreakdownDonut } from '../components/charts/TaskBreakdownDonut';
import { FiltersBar } from '../components/FiltersBar';
import { SessionDetailDrawer } from '../components/SessionDetailDrawer';
import { SessionsFilterBar } from '../components/SessionsFilterBar';
import { SessionsTable } from '../components/SessionsTable';
import { UserDetailDrawer } from '../components/UserDetailDrawer';
import { useFilters } from '../filters/FiltersContext';

const PAGE_SIZE = 25;
const QUERY_KEYS = ['outcome-series', 'task-breakdown', 'sessions', 'session-detail'];

export function Sessions(): JSX.Element {
  const { token, signOut } = useToken();
  const { range, origin } = useFilters();
  const [offset, setOffset] = useState(0);
  const [sortBy, setSortBy] = useState<SessionSortField>('started_at');
  const [sortDir, setSortDir] = useState<SessionSortDir>('desc');
  const [filters, setFilters] = useState<SessionFilters>(EMPTY_SESSION_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  // Stack a user drawer on top when the user clicks the user_id pill in
  // the session drawer. Closing it returns to the session drawer.
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const activeFilterCount = countActiveSessionFilters(filters);
  const filterFingerprint = JSON.stringify(filters);

  const window: DashboardWindow = { from: range.from, to: range.to, origin };
  const tokenKey = token?.slice(-8);

  const outcomeQuery = useQuery({
    queryKey: ['outcome-series', range.from, range.to, origin, tokenKey],
    queryFn: () => fetchOutcomeSeries(window, token),
    enabled: !!token,
  });

  const taskQuery = useQuery({
    queryKey: ['task-breakdown', range.from, range.to, origin, tokenKey],
    queryFn: () => fetchTaskBreakdown(window, token),
    enabled: !!token,
  });

  const sessionsQuery = useQuery({
    queryKey: [
      'sessions',
      range.from,
      range.to,
      origin,
      PAGE_SIZE,
      offset,
      sortBy,
      sortDir,
      filterFingerprint,
      tokenKey,
    ],
    queryFn: () =>
      fetchSessions(
        window,
        { limit: PAGE_SIZE, offset, sortBy, sortDir, filters },
        token,
      ),
    enabled: !!token,
  });

  const allQueries = [outcomeQuery, taskQuery, sessionsQuery];
  const authError = allQueries
    .map((q) => q.error)
    .find((e): e is ApiAuthError => e instanceof ApiAuthError);
  if (authError) {
    return (
      <SignInScreen
        reason={`Token rejected by the worker (HTTP ${authError.status}): ${authError.message}`}
      />
    );
  }

  const isRefreshing = allQueries.some((q) => q.isFetching);

  return (
    <section className="overview">
      <FiltersBar
        isRefreshing={isRefreshing}
        invalidateKeys={QUERY_KEYS}
      />

      {allQueries.some((q) => q.isError) && !authError && (
        <div className="overview-error">
          Failed to load some data.{' '}
          <button type="button" onClick={signOut}>Sign out</button>
        </div>
      )}

      <section className="chart-section">
        <header>
          <h2>Sessions by outcome</h2>
          <p className="chart-subtitle">
            Sessions stacked by v5 classifier outcome, attributed to their first day. Unclassified
            sessions are excluded.
          </p>
        </header>
        <OutcomeStackedBarChart
          points={outcomeQuery.data?.points ?? []}
          loading={outcomeQuery.isLoading}
        />
      </section>

      <section className="chart-section">
        <header>
          <h2>Task mix</h2>
          <p className="chart-subtitle">
            v5 classifier <code>primary_task</code> across cone-aggregated sessions in the window.
            Click a session in the table below to see its full journey.
          </p>
        </header>
        <TaskBreakdownDonut
          tasks={taskQuery.data?.tasks ?? []}
          unclassified={taskQuery.data?.unclassifiedSessions ?? 0}
          loading={taskQuery.isLoading}
        />
      </section>

      <section className="chart-section">
        <header>
          <h2>Recent sessions</h2>
          <p className="chart-subtitle">
            Newest sessions first. Click any row to open the journey drawer. Toggle the
            filters panel to narrow by task, outcome, feel, started date, duration, or
            event count — applying any classification filter naturally excludes
            unclassified sessions.
          </p>
        </header>
        <div className="sessions-filter-toggle-row">
          <button
            type="button"
            className={`sessions-filter-toggle${filtersOpen ? ' is-open' : ''}`}
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            aria-controls="sessions-filter-bar"
          >
            <span>{filtersOpen ? '▾' : '▸'} Filters</span>
            {activeFilterCount > 0 && (
              <span className="sessions-filter-toggle-badge">{activeFilterCount}</span>
            )}
          </button>
          {!filtersOpen && activeFilterCount > 0 && (
            <button
              type="button"
              className="filter-clear"
              onClick={() => {
                setFilters(EMPTY_SESSION_FILTERS);
                setOffset(0);
              }}
            >
              Clear filters
            </button>
          )}
        </div>
        {filtersOpen && (
          <div id="sessions-filter-bar">
            <SessionsFilterBar
              filters={filters}
              onChange={(next) => {
                setFilters(next);
                setOffset(0);
              }}
            />
          </div>
        )}
        <SessionsTable
          sessions={sessionsQuery.data?.sessions ?? []}
          total={sessionsQuery.data?.total ?? 0}
          limit={PAGE_SIZE}
          offset={offset}
          loading={sessionsQuery.isLoading}
          sortBy={sortBy}
          sortDir={sortDir}
          onSortChange={(nextBy, nextDir) => {
            setSortBy(nextBy);
            setSortDir(nextDir);
            setOffset(0);
          }}
          onSelect={(s: SessionSummary) => setSelected(s.sessionId)}
          onPageChange={(nextOffset) => setOffset(nextOffset)}
        />
      </section>

      <SessionDetailDrawer
        sessionId={selected}
        onClose={() => setSelected(null)}
        onSelectUser={(userId) => setSelectedUser(userId)}
      />
      <UserDetailDrawer
        userId={selectedUser}
        onClose={() => setSelectedUser(null)}
        onSelectSession={(sessionId) => setSelected(sessionId)}
      />
    </section>
  );
}
